// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Facility, Meta } from './types';

export interface Dataset {
  facilities: Facility[];
  meta: Meta;
  bySlug: Map<string, Facility>;
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Could not load ${url} (HTTP ${res.status})`);
  return (await res.json()) as T;
}

export async function loadDataset(signal?: AbortSignal): Promise<Dataset> {
  const [facilities, meta] = await Promise.all([
    fetchJson<Facility[]>('data/facilities.json', signal),
    fetchJson<Meta>('data/meta.json', signal),
  ]);
  const bySlug = new Map(facilities.map((f) => [f.slug, f]));
  return { facilities, meta, bySlug };
}

let geoPromise: Promise<GeoJSON.FeatureCollection> | null = null;
export function loadGeo(signal?: AbortSignal): Promise<GeoJSON.FeatureCollection> {
  if (!geoPromise) {
    geoPromise = fetchJson<GeoJSON.FeatureCollection>('data/states.geojson', signal).catch((err) => {
      geoPromise = null;
      throw err;
    });
  }
  return geoPromise;
}

/** Search facilities by name or company. */
export function searchFacilities(all: Facility[], query: string, limit = 8): Facility[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: Array<{ f: Facility; score: number }> = [];
  for (const f of all) {
    const name = f.name.toLowerCase();
    const co = f.company.toLowerCase();
    let score = -1;
    if (name === q || co === q) score = 100;
    else if (name.startsWith(q)) score = 82;
    else if (co.startsWith(q)) score = 74;
    else if (name.includes(q)) score = 55;
    else if (co.includes(q)) score = 48;
    else if (f.sector.toLowerCase().includes(q)) score = 30;
    if (score > 0) scored.push({ f, score: score + Math.min(f.covered, 5e6) / 1e8 });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.f);
}
